// Copyright (C) 2025, Meritic Inc.

import React from "react";
import {
  VStack,
  Text,
  Box,
  Radio,
  RadioGroup,
  Stack,
  Badge,
  Alert,
  AlertIcon,
  Code,
} from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { AnalysisTreeProto } from "build-ts/analysis";

// Tree Selection Step
interface TreeSelectionStepProps {
  trees: Header[];
  selectedTree: Header | null;
  onSelectionChange: (tree: Header | null) => void;
}

export const TreeSelectionStep: React.FC<TreeSelectionStepProps> = ({
  trees,
  selectedTree,
  onSelectionChange,
}) => {
  const handleTreeSelection = (treeId: string) => {
    const tree = trees.find(t => t.id === treeId);
    if (!tree || !tree.id) return;

    // Toggle selection - if already selected, deselect; otherwise select
    if (selectedTree?.id === treeId) {
      onSelectionChange(null);
    } else {
      onSelectionChange(tree);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <Text fontSize="sm" color="gray.600">
        Select one analysis tree for TIGA agent:
      </Text>

      <RadioGroup value={selectedTree?.id || ""} onChange={handleTreeSelection}>
        <Stack spacing={3} maxH="300px" overflowY="auto">
          {trees.length === 0 ? (
            <Box p={4} textAlign="center" color="gray.500">
              <Text>No analysis trees available</Text>
            </Box>
          ) : (
            trees.map((tree) => (
              <Radio
                key={tree.id}
                value={tree.id || ""}
                isDisabled={!tree.id}
                p={3}
                border="1px solid"
                borderColor={selectedTree?.id === tree.id ? "blue.300" : "gray.200"}
                borderRadius="md"
                bg={selectedTree?.id === tree.id ? "blue.50" : "white"}
                cursor="pointer"
                _hover={{ borderColor: "blue.200" }}
                w="100%"
                display="flex"
                alignItems="flex-start"
              >
                <VStack align="start" pl={3} w="100%">
                  <Badge colorScheme="blue" size="sm">
                      ID: {tree.id || "N/A"}
                  </Badge>
                  <Text fontWeight="semibold" mb={0}>{tree.name || "Unnamed Tree"}</Text>
                  <Text fontSize="sm" color="gray.600" mb={0}>{tree.description || "No description available"}</Text>
                </VStack>
              </Radio>
            ))
          )}
        </Stack>
      </RadioGroup>

      {selectedTree && (
        <Box p={3} bg="green.50" borderRadius="md">
          <Text fontSize="sm" fontWeight="semibold" color="green.800" mb={2}>
            Selected Tree:
          </Text>
          <Text fontSize="sm" color="green.700">
            {selectedTree.name || "Unnamed Tree"} (ID: {selectedTree.id})
          </Text>
        </Box>
      )}
    </VStack>
  );
};

// Review Step
interface ReviewStepProps {
  agentType: string;
  basePrompt: string;
  selectedTree: Header | null;
  selectedTreeData: AnalysisTreeProto | null;
  generatedPrompt: string | null;
  isGenerating: boolean;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  agentType,
  basePrompt,
  selectedTree,
  selectedTreeData,
  generatedPrompt,
  isGenerating,
}) => {
  return (
    <VStack spacing={4} align="stretch">
      <Text fontSize="sm" color="gray.600">
        Review your configuration and generate the system prompt:
      </Text>

      <Box p={4} bg="gray.50" borderRadius="md">
        <Text fontSize="sm" fontWeight="semibold" mb={2}>Agent Type:</Text>
        <Badge colorScheme="blue">{agentType}</Badge>
      </Box>

      <Box p={4} bg="gray.50" borderRadius="md">
        <Text fontSize="sm" fontWeight="semibold" mb={2}>Base Prompt:</Text>
        <Code fontSize="xs" whiteSpace="pre-wrap" p={2} bg="white">
          {basePrompt}
        </Code>
      </Box>

      {selectedTree && (
        <Box p={4} bg="gray.50" borderRadius="md">
          <Text fontSize="sm" fontWeight="semibold" mb={2}>Selected Tree:</Text>
          <Text fontSize="xs">• {selectedTree.name} (ID: {selectedTree.id})</Text>
          {selectedTree.description && (
            <Text fontSize="xs" color="gray.600" mt={1}>
              Description: {selectedTree.description}
            </Text>
          )}
          {selectedTreeData && (
            <Text fontSize="xs" color="green.600" mt={1}>
              ✓ Tree data loaded successfully
            </Text>
          )}
        </Box>
      )}

      {generatedPrompt && (
        <Box p={3} bg="green.50" borderRadius="md">
          <Text fontSize="sm" fontWeight="semibold" mb={2} color="green.800">
            Generated System Prompt:
          </Text>
          <Box
            maxH="200px"
            overflowY="auto"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="md"
            bg="white"
            position="relative"
            mb={3}
          >
            <Code
              fontSize="xs"
              whiteSpace="pre-wrap"
              p={2}
              display="block"
              w="100%"
              fontFamily="mono"
              lineHeight="1.4"
            >
              {generatedPrompt}
            </Code>
          </Box>
        </Box>
      )}

      {isGenerating && (
        <Alert status="info">
          <AlertIcon />
          <Text mb={0}>Generating system prompt...</Text>
        </Alert>
      )}
    </VStack>
  );
};
